/* ============================================================
   EXON MYSTERY CARDS — the sealed hand, and the write behind the pick.

   The dealt-hand opening the owner approved, driven onto the shell in
   snippets/drops-mystery-cards.liquid at every mount that renders it
   (popup, drawer, cart page). One island for all of them, because they
   answer the same question from the same fact and a second reader is a
   second answer — the same rule drops-ticket.js runs on.

   THIS FILE CALLS NOTHING. drops.js is and stays the sole caller of the
   drops API; drops-bar.js is and stays the registry. The read arrives
   through the registry; the WRITE goes back out through drops.js. This
   island never touches the network, which is why the pick below is a
   promise handed to somebody else rather than a fetch.

   ── LOAD ORDER, AND WHY IT IS NOT AN ACCIDENT ─────────────────────
   This file reads the item drops.js registered through the bar island's
   registry, exactly as drops-ticket.js does, so it MUST execute after
   drops-bar.js. Its tag in layout/theme.liquid is `defer` and sits after
   drops-ticket.js: defer preserves DOCUMENT ORDER at execution, so
   position in the file is the guarantee. `async` would break this
   SILENTLY — it executes on arrival, so it would beat drops-bar.js on a
   fast connection and lose on a slow one. That defect shows only on a
   real page, and only sometimes.

   ── THE CONTRACT THIS SURFACE READS ───────────────────────────────
   The E3 choice block drops.js normalises out of `/state`
   (drops_private.choice_member_state, migrations 20261002006730/006740):

     choice.attempt      = {mystery_attempt_id, attempt_no, state:'dealt',
                            slot_count, hand_digest, hand_expires_at,
                            advertised:[{title, art_url?, accent?}]}
     choice.last_attempt = {mystery_attempt_id, outcome:'win'|'blank'|'expired',
                            slot, choice_grant_id, ...}
     choice.grants       = [{choice_grant_id, mechanic, state, variant_gid,
                             no_expiry, code_available, code, ...}]

   THE SEALED MAP IS NOT ON THE WIRE, and that is the invariant rather than a
   preference. `attempt` carries the slot COUNT and the hand DIGEST; which
   card is behind which slot is not projected at any state, so there is
   nothing here to leak. `advertised` is the campaign's PUBLIC prize set,
   identical for every member, which is exactly why showing it face-up during
   the opening reveals nothing about this hand.

   OPAQUE SLOTS ARE LOCAL REPRESENTATIONS OF A COUNT. The cards on screen are
   slot 0..slot_count-1, in that order, and a pick submits THAT SERVER INTEGER
   unchanged. No slot is ever associated with a prize anywhere in this file.

   `last_attempt` is what a reload reads: the open hand goes null at pick, so
   without it a blank would be indistinguishable from never having played.

   ── WHAT THE OWNER'S CURRENT DECISION CHANGED ─────────────────────
   The prototype this port comes from assumed ONE PICK EVER and no blanks.
   Both are superseded: a configured deck may deal a BLANK, a member may make
   REPEATED attempts, and repeated committed wins are permitted. So there is
   no page-wide "already played" lock here. What is locked is ONE IN-FLIGHT
   OPERATION; whether another attempt is available is derived from real state
   (`choice.attempt`), never from a client-side flag.

   A RECORDED WIN NEVER EXPIRES. `no_expiry` is the grant's own fact and the
   family's discriminated invariant; a saved benefit stays visible and
   attachable after the campaign's acquisition window closes.

   ── THE STATES, enumerated from what can actually be reached ──────
     an open attempt                 the sealed hand, pickable
     a resolved attempt, win         the receipt, naming the won prize
     a resolved attempt, blank       the honest blank, and another attempt
                                     when the server still offers one
     no attempt and no last attempt  the folded line: deal on the tap
     not a mystery campaign          NOTHING AT ALL
     no write path installed         NOTHING AT ALL, and a fault

   The last one is deliberate. A hand a customer can tap that cannot
   record the tap is worse than no hand: it takes a choice it cannot
   keep. Redeemed, expired and not-entitled are all ABSENCE, same as the
   ticket — the absence IS the state.

   -- THE FOLDED LINE IS THE SHARED ROW (EXO-4824) -------------------
   Every mount sits inside a CONTROLLED fold row (snippets/fold-row.liquid,
   key 'mystery'; window.ExonFold, assets/fold.js). The row is the one
   control. This island owns its state and its side effects, so the row's
   tap is a REQUEST (`xf:toggle`) the island answers by COMMITTING
   (ExonFold.open / close); whether the hand is open is read back from the
   row on every paint, never kept in a flag of our own. Each paint, and each
   state the panel is put in, also writes the folded line through
   ExonFold.set: whether it shows at all, its title, its clock or state
   word, its status and its icon. All of its copy is read from the shell.
   Contract: docs/exo-4667/DROP-ADOPTION.md; rulings R4, R5, R7, R8, R10 in
   docs/exo-4824/BUILD-PLAN.md.

   FOLDED IS NOT GONE. A folded hand is available (the ticket still stands
   down for it, the folded line keeps its clock ticking and its state), but
   it is not on screen: no measurement, no choreography and no announcement
   happen while it is folded, and opening plays the surface exactly as a
   shell opening does. Folding cancels ANIMATION timers only; the pick's
   watchdog, held and recovery timers and the transaction itself belong to
   the write, and a fold that would lose a write in flight is refused.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropMysteryInit) return;
  window.__exonDropMysteryInit = true;
  window.EXON = window.EXON || {};

  /* K is a DURATION multiplier and the owner set it after watching the
     surface move: 1.54 is 0.65x playback. His earlier "quick" brief was
     written before he had seen it, and this supersedes it. Motion rides K;
     the network does not, because a real clock does not slow down because
     our choreography did. */
  var K = 1.54;
  var FLIGHT = 500;      /* the picked card's flight to centre, in OUR ms — scaled by K */
  /* The wait becomes visible only once it has outlasted the flight, so a normal
     confirmation never shows a waiting state to anybody.

     THIS THRESHOLD IS DERIVED, NOT TYPED, and the first version got it wrong in a
     way worth leaving a note about: it was a flat 460 real ms compared against a
     flight that is itself scaled by K, so at K=1.54 the flight really takes 770ms
     and the "only past the flight" cue was landing 310ms BEFORE the card reached
     centre — on top of a still-moving card, with two comments elsewhere in this
     file cheerfully claiming the opposite. The threshold has to be measured in the
     same units as the thing it is compared to. */
  function holdAfter() { return FLIGHT * K + 40; }
  /* the outer bound on a write that never answers — see the timeout in pick() */
  var WRITE_TIMEOUT = 20000;
  var BEATS = ['reveal', 'flip', 'shuffle', 'spread'];

  /* ONE PICK PER PAGE, NOT PER PANEL. Every mount on the page shares this, and it
     is deliberately module scope rather than a panel field: the popup, the drawer
     and the cart page are three panels over ONE entitlement, and the thing being
     spent is the ATTEMPT, and the server is what records it. */
  /* ONE IN-FLIGHT OPERATION, NOT ONE PICK EVER. The popup, the drawer and the
     cart page are three panels over ONE session, so a write started in one must
     not be joined by a second from another — but whether ANOTHER ATTEMPT is
     available afterwards is the server's answer (`choice.attempt`), never a
     client-side "already played" flag. The owner's current decision permits
     repeated attempts, blanks and repeated committed wins; a blanket lock here
     would silently contradict all three. */
  var writeInFlight = false;

  /* ---------------- faults ----------------
     Same attribute and same console channel as drops.js, drops-bar.js and
     drops-ticket.js, so an operator reads one signal whichever part of the
     stack broke. The customer surface still degrades to nothing; what must
     never happen is "broken" and "this customer has no drop" looking
     identical from outside. */
  function fault(name, detail) {
    try {
      var h = document.documentElement;
      var cur = h.getAttribute('data-drops-fault') || '';
      if ((' ' + cur + ' ').indexOf(' ' + name + ' ') === -1) {
        h.setAttribute('data-drops-fault', cur ? cur + ' ' + name : name);
      }
    } catch (e) { /* no document is not a fault we can report */ }
    /* The attribute dedupes; so does this. A fault that fires from repaint() —
       and the missing write path does — would otherwise write one console line per
       drops-state, cart and shell event for the whole page life, burying the
       operator signal this channel exists to carry under its own repetition. */
    if (REPEATS[name]) {
      if (said[name]) return;
      said[name] = true;
    }
    try { console.error('[exon:drops] ' + name, detail || ''); } catch (e) {}
  }
  var said = {};
  /* ONLY THE REPAINT-CLASS FAULTS ARE DEDUPED, and the distinction is the point.
     These fire from repaint(), which runs on every drops-state, cart and shell
     event, so without a latch one missing write path buries the console under
     hundreds of identical lines. TRANSACTIONAL faults are the opposite: a failed
     pick deliberately returns a retryable hand, so a second failure is a SECOND
     EVENT with its own error and possibly its own campaign, and swallowing it
     would hide the retry loop the operator most needs to see. */
  var REPEATS = {
    mystery_bar_unavailable: 1,
    mystery_write_unavailable: 1,
    mystery_not_renderable: 1,
    mystery_cards_incomplete: 1,
    mystery_cards_mismatched: 1,
    mystery_cards_duplicate_slots: 1,
    mystery_cards_oversized: 1
  };

  /* ---------------- tiny helpers ---------------- */
  function all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function setText(node, value) {
    if (!node) return;
    if (node.textContent !== value) node.textContent = value;
  }
  function show(node, on) { if (node) node.hidden = !on; }
  /* text that is about to become markup: the row's title and clock carry an
     amount or a time in a bidi-isolated run, so they are written as HTML, and
     every word around that run is escaped first */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(agorot) {
    if (typeof agorot !== 'number' || !isFinite(agorot)) return '';
    return '₪' + ((Math.round(agorot) / 100).toString());
  }

  /* A server-supplied string is about to become part of a CSS url(). Anything
     that could close the url() or the declaration is refused outright rather
     than escaped: art is decoration, so dropping a suspicious one costs a
     placeholder tile, and there is no value in being clever here. */
  function coverValue(url) {
    if (typeof url !== 'string' || !url) return '';
    if (!/^(https?:\/\/|\/)[^\s"')\\]+$/.test(url)) return '';
    return 'url("' + url + '")';
  }
  /* the accent rides straight into rgba(), so it is three numbers or nothing */
  function accentValue(a) {
    if (typeof a !== 'string') return '';
    return /^\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*$/.test(a) ? a : '';
  }

  /* ============================================================
     derive — PURE. No DOM, no globals, no clock of its own: the normalised
     choice block in, what the surface should show out, or the REASON there
     is nothing to show.

     Split out for the same reason the bar splits validate and compose, and
     drops-ticket.js splits this: the decisions worth testing are these, and
     they must be reachable without a DOM.

     ctx = { mechanic, windowEndAt, action,
             conditionMinAgorot, eligibleSubtotalAgorot,
             minimumQuantity, eligibleQuantity }
     ============================================================ */
  function derive(choice, nowMs, countdown, ctx) {
    ctx = ctx || {};
    /* not a mystery-cards campaign — somebody else's item, not an incident */
    if (ctx.mechanic !== 'mystery_cards') return { view: null, reason: 'not_mystery' };
    if (!choice || typeof choice !== 'object') return { view: null, reason: 'no_choice' };

    var action = (ctx.action && typeof ctx.action === 'object') ? ctx.action : { state: 'idle' };
    var busy = action.pending === true;
    var min = (typeof ctx.conditionMinAgorot === 'number' && isFinite(ctx.conditionMinAgorot) &&
      ctx.conditionMinAgorot >= 0 && Math.floor(ctx.conditionMinAgorot) === ctx.conditionMinAgorot)
      ? ctx.conditionMinAgorot : null;
    var eligible = (typeof ctx.eligibleSubtotalAgorot === 'number' &&
      isFinite(ctx.eligibleSubtotalAgorot) && ctx.eligibleSubtotalAgorot >= 0 &&
      Math.floor(ctx.eligibleSubtotalAgorot) === ctx.eligibleSubtotalAgorot)
      ? ctx.eligibleSubtotalAgorot : null;
    /* THE AUTHORED UNIT MINIMUM, the same conjunction as the goal rail: a
       known integer minimum and a measured eligible quantity below it lock
       the hand even when the amount is met. No minimum (an older server) is
       today's amount-only rule exactly; an unknown quantity never locks. */
    var minQty = (typeof ctx.minimumQuantity === 'number' && isFinite(ctx.minimumQuantity) &&
      ctx.minimumQuantity >= 1 && Math.floor(ctx.minimumQuantity) === ctx.minimumQuantity)
      ? ctx.minimumQuantity : null;
    var qty = (typeof ctx.eligibleQuantity === 'number' && isFinite(ctx.eligibleQuantity) &&
      ctx.eligibleQuantity >= 0 && Math.floor(ctx.eligibleQuantity) === ctx.eligibleQuantity)
      ? ctx.eligibleQuantity : null;
    var itemsShort = min !== null && eligible !== null && minQty !== null && qty !== null && qty < minQty;
    var locked = (min !== null && eligible !== null && eligible < min) || itemsShort;
    /* amount-only: 0 when only the items are short, never a borrowed gap */
    var remaining = locked ? Math.max(0, min - eligible) : null;
    var remainingItems = itemsShort ? minQty - qty : null;

    /* One source of time — the CAMPAIGN's window, formatted by the bar
       island's countdown, the same field the strip's compose() uses to decide
       the item exists at all. Empty at zero, so a closed campaign reads as
       gone rather than as a negative number. */
    var left = countdown ? (countdown(ctx.windowEndAt || null, nowMs) || '') : '';

    /* THE ADVERTISED SET is the campaign's public prize list, identical for
       every member. It is used INDEPENDENTLY of the hand: it names what is in
       the deck, and it is never bound to a slot. */
    var advertised = [];
    var source = (choice.attempt && Array.isArray(choice.attempt.advertised))
      ? choice.attempt.advertised :
      ((choice.public && Array.isArray(choice.public.advertised)) ? choice.public.advertised : []);
    for (var a = 0; a < source.length; a++) {
      var ad = source[a];
      var nm = ad.title || ad.name || '';
      if (!nm) continue;
      advertised.push({
        name: String(nm),
        sub: ad.sub || ad.subtitle || '',
        cover: coverValue(ad.art_url),
        accent: accentValue(ad.accent),
        variant_gid: ad.variant_gid || ''
      });
    }
    /* the biggest prize is named in the header, and it is the campaign's
       claim rather than ours: first in the advertised list, by contract */
    var top = advertised.length ? advertised[0].name : '';

    /* THE RESOLVED ATTEMPT, and the grant it produced. A reload lands here. */
    var last = choice.last_attempt;
    var grantsById = Object.create(null);
    var grants = Array.isArray(choice.grants) ? choice.grants : [];
    for (var g = 0; g < grants.length; g++) grantsById[grants[g].choice_grant_id] = grants[g];

    var attempt = choice.attempt;
    if (!attempt && last && (last.outcome === 'win' || last.outcome === 'blank')) {
      var grant = last.choice_grant_id ? grantsById[last.choice_grant_id] : null;
      var face = null;
      if (last.outcome === 'win') {
        /* the won prize names itself through the ADVERTISED entry the grant's
           variant matches. Nothing is invented: a grant whose variant matches
           no advertised entry names nothing rather than borrowing a name. */
        for (var f = 0; f < advertised.length; f++) {
          if (grant && advertised[f].variant_gid && advertised[f].variant_gid === grant.variant_gid) {
            face = advertised[f];
          }
        }
        if (grant && grant.display && grant.display.variant_gid === grant.variant_gid) {
          face = { name: grant.display.title || grant.display.name || '',
            sub: grant.display.sub || grant.display.subtitle || '',
            cover: coverValue(grant.display.art_url) };
        }
      }
      return {
        reason: '',
        view: {
          id: last.mystery_attempt_id,
          top: top,
          left: left,
          faces: [],
          slots: [],
          sealedOpening: false,
          locked: false,
          remaining_agorot: null,
          remaining_items: null,
          /* `pick` is the receipt the surface lands in. Its slot is the
             server's own recorded slot, which is a fact about THIS member's
             resolved attempt and reveals nothing about any other. */
          pick: {
            slot: last.slot,
            outcome: last.outcome,
            name: face ? face.name : '',
            sub: face ? face.sub : '',
            cover: face ? face.cover : '',
            choice_grant_id: last.choice_grant_id || null,
            /* READINESS AND DISCLOSURE ARE TWO FACTS: a committed win whose
               provider issuance is pending shows PENDING and names no code. */
            code_available: !!(grant && grant.code_available),
            attachable: !!(grant && grant.code_available &&
              typeof grant.code === 'string' && grant.code !== ''),
            no_expiry: !!(grant && grant.no_expiry)
          },
          /* ANOTHER ATTEMPT IS THE SERVER'S ANSWER, NOT OURS. The surface
             offers one only when a fresh hand actually arrives; until then it
             says nothing about whether one is available. */
          again: false
        }
      };
    }

    if (!attempt) {
      /* No open hand and nothing resolved: the folded line, which deals on
         the customer's own tap through the real caller. */
      return {
        reason: '',
        view: {
          id: '', top: top, left: left, faces: [], slots: [],
          sealedOpening: false, pick: null, again: !busy && !locked,
          locked: locked, remaining_agorot: remaining,
          remaining_items: remainingItems
        }
      };
    }

    /* the hand's own deadline, independent of the campaign window */
    var handEnd = Date.parse(attempt.hand_expires_at);
    if (isFinite(handEnd) && handEnd <= nowMs) {
      return { view: null, reason: 'hand_expired' };
    }

    /* THE SLOTS ARE A COUNT, RENDERED. slot n on screen is slot n on the
       wire; there is no id to collide, trim or dedupe because the server
       issues an integer range rather than opaque strings. */
    var slots = [];
    for (var i = 0; i < attempt.slot_count; i++) slots.push(i);

    /* THE FACES DEALT DURING THE OPENING. The approved opening deals every
       advertised prize face up, then shuffles, then the customer picks a
       face-DOWN card. That choreography needs one face per card, so it plays
       only when the advertised set and the hand are the same size.

       WHEN THEY ARE NOT, THE OPENING IS SEALED RATHER THAN FAKED. A deck may
       legitimately advertise fewer tiers than a hand holds cards; dealing a
       repeated face would state that the same prize is in the hand twice, and
       padding with a blank face would state a blank is guaranteed. Neither is
       a fact this client has. The advertised set is still named in the header
       and the rule line; only the face-up beat stands down. */
    var faces = [];
    var sealed = advertised.length !== slots.length;
    for (var k = 0; k < slots.length; k++) {
      faces.push(sealed
        ? { name: '', sub: '', cover: '', accent: '' }
        : advertised[k]);
    }

    return {
      reason: '',
      view: {
        id: attempt.mystery_attempt_id,
        top: top,
        left: left,
        faces: faces,
        slots: slots,
        sealedOpening: sealed,
        locked: locked,
        remaining_agorot: remaining,
        remaining_items: remainingItems,
        pick: null,
        again: false
      }
    };
  }

  /* ------------------------------------------------------------
     THE ONE READ — the registry the strip installs. Requiring all four
     members keeps the dependency to ONE object: no bar means no items to
     read anyway, so no bar means no hand, which is correct degradation
     rather than a special case.
     ------------------------------------------------------------ */
  function bar() {
    var b = window.EXON.bar;
    return (b && typeof b.countdown === 'function') ? b : null;
  }
  /* THE ONE READ. The hand is a fact about this SESSION, not an item on the
     strip, so it arrives through drops.js's normalised choice block rather
     than through the bar registry. The registry is still the one clock: the
     countdown that formats this surface's deadline is the same one the strip
     uses to decide the campaign exists at all, so the two can never disagree. */
  function reader() {
    var d = window.EXON.drops;
    return (d && typeof d.getChoice === 'function') ? d : null;
  }

  /* THE WRITE PATH. drops.js owns every call to the drops API, so the pick is
     a method it exposes and this island holds no endpoint, no token and no
     fetch. If it is not installed, there is no hand — see the header. */
  function writer() {
    var d = window.EXON.drops;
    return (d && typeof d.pickMysteryCard === 'function' &&
      typeof d.dealMysteryHand === 'function') ? d : null;
  }

  function current(nowMs) {
    var b = bar();
    if (!b) return { view: null, reason: 'no_registry' };
    var d = reader();
    if (!d) return { view: null, reason: 'no_client' };
    var s = state();
    var progress = typeof d.getChoiceProgress === 'function' ? d.getChoiceProgress() : null;
    try {
      return derive(d.getChoice(), nowMs, b.countdown, {
        mechanic: s ? s.mechanic : null,
        windowEndAt: s ? s.window_end_at : null,
        action: typeof d.getChoiceAction === 'function' ? d.getChoiceAction() : null,
        conditionMinAgorot: progress ? progress.minimum_subtotal_agorot :
          ((s && s.condition) ? s.condition.minimum_subtotal_agorot : null),
        eligibleSubtotalAgorot: progress ? progress.eligible_subtotal_agorot : null,
        minimumQuantity: progress ? progress.minimum_quantity : null,
        eligibleQuantity: progress ? progress.eligible_quantity : null
      });
    } catch (e) {
      fault('mystery_read_failed', e);
      return { view: null, reason: 'read_failed' };
    }
  }

  /* ------------------------------------------------------------
     Reporting an absence. An entitled member looking at a page with no hand
     on it is a different incident from a visitor who has no drop, and from
     outside they are identical. Exactly two reasons are legitimate: this is
     not a mystery-cards campaign, and the window has closed.
     ------------------------------------------------------------ */
  function state() {
    var d = window.EXON.drops;
    return (d && d.state) || null;
  }
  function entitled() {
    var s = state();
    return !!(s && s.entitled);
  }
  function windowClosed(nowMs) {
    var b = bar();
    var s = state();
    if (!b || !s || !s.window_end_at) return false;
    return b.countdown(s.window_end_at, nowMs) === null;
  }

  function report(reason, nowMs) {
    if (!reason) return;
    if (reason === 'not_mystery') return;       /* a different campaign type */
    if (reason === 'no_choice') return;         /* no choice session at all */
    if (reason === 'no_client') return;
    if (!entitled()) return;                    /* no drop is not an incident */
    if (reason === 'read_failed') return;       /* already emitted, with the throw */
    if (reason === 'no_registry') { fault('mystery_bar_unavailable', ''); return; }
    if (windowClosed(nowMs)) return;            /* absence IS the state */
    var s = state();
    var id = (s && s.campaign_id) || '';
    /* A HAND THAT RAN OUT IS NOT A DEFECT. The server expires unpicked hands
       and `/state` stops projecting them; the surface simply has nothing to
       deal until the member asks for another. */
    if (reason === 'hand_expired') return;
    fault('mystery_not_renderable', id);
  }

  /* the row's lock icon, as fold.js keeps its own icons: markup, not copy
     (the same glyph as snippets/icon-upsell.liquid 'lock') */
  var LOCK_ICON = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

  /* THE ROW'S STATUS PER PANEL STATE (BUILD-PLAN.md R8). 'action' is a hand
     still waiting for the customer (the sealed line, the pick, a write being
     saved, a failed one to try again); a recorded win and a recorded blank
     are both disclosed outcomes; a win whose issuance has not landed is NOT
     'done', because a benefit not yet issued is not a disclosed reward. */
  var ROW_STATUS = {
    deal: 'action', tease: 'action', live: 'action', held: 'action', fail: 'action',
    locked: 'locked', won: 'done', blank: 'done', pending: 'idle'
  };
  /* the states whose folded line carries the campaign clock: the promise
     (the sealed hand) and the pick, the two that have a deadline to state */
  var CLOCK_STATES = { deal: 1, tease: 1, live: 1 };

  function fold() {
    var f = window.ExonFold;
    return (f && typeof f.set === 'function') ? f : null;
  }

  /* ============================================================
     THE PANEL — one mount's worth of surface: the shell, the deck it
     deals, the four beats, and the pick.
     ============================================================ */
  function Panel(mount) {
    var p = mount.querySelector('[data-dmc-panel]');
    var stage = mount.querySelector('[data-dmc-stage]');
    var tpl = mount.querySelector('[data-dmc-card-tpl]');
    if (!p || !stage || !tpl) return null;

    var titles = mount.querySelector('[data-dmc-titles]');
    var words = mount.querySelector('[data-dmc-words]');
    var attachBtn = mount.querySelector('[data-dmc-attach]');
    var keptNode = mount.querySelector('[data-dmc-kept]');
    var noexpNode = mount.querySelector('[data-dmc-noexp]');
    var surface = mount.getAttribute('data-dmc-surface') || 'popup';

    /* keep in step with drops-anchor.js Panel()'s row lookup (EXO-4824 D-1) */
    /* THE ROW this mount sits in, found once: the mount is never moved out
       of it, and a mount rendered without one (no fold row on the page)
       simply has no folded line to write and no control to answer. */
    var row = mount.closest ? mount.closest('[data-xf]') : null;
    var rowBtn = null;
    if (row) {
      for (var rc = row.firstElementChild; rc; rc = rc.nextElementSibling) {
        if (rc.classList && rc.classList.contains('xf-row')) { rowBtn = rc; break; }
      }
    }
    var rowState = rowBtn ? rowBtn.querySelector('[data-xf-state]') : null;
    var api = null;        /* the object this Panel returns, for its own handlers */

    /* OPEN IS THE CUSTOMER'S MOVE (master section 7: both choice mechanics
       start folded to one line carrying the promise and the clock), and it is
       the ROW's state, per mount: the popup teases and the cart chooses, so
       each surface's row answers for itself. Read back from the row on every
       paint, never a flag of ours, so the controller's own silent fold at boot
       (fold.js ready()) and a group close by another row are both simply true
       here. Without the controller the row is server-rendered open (the no-JS
       floor), and that is what this reads. */
    function rowOpen() {
      if (!row) return false;
      var f = fold();
      return f ? f.isOpen(row) === true : row.getAttribute('data-xf-open') === 'true';
    }
    /* a mount with no row has nothing to be folded in */
    function folded() { return !!row && !rowOpen(); }

    var cards = [];        /* the card nodes, in prize order while faces are up */
    var slotIds = [];      /* the server's own slot integers, 0..slot_count-1 */
    var slotOf = [];       /* card index → server slot. Written only once faces are gone. */
    var seq = [];          /* seq[position] = card index; the shuffle rewrites it */
    var cfg = null;
    var timers = [];
    var picked = false;
    var committing = false;
    var built = '';        /* the view signature the current deck was built from */
    var played = '';       /* the view signature the opening was PLAYED for */
    var opening = false;   /* an opening is mid-flight right now */
    var narrow = true;     /* which of the two approved frames this mount uses */
    var locked = false;    /* measured campaign checkpoint, shared by all taps */

    /* IS ANYBODY LOOKING AT THIS MOUNT?

       Two different questions on two different surfaces, and the first version
       answered neither: it asked `!mount.hidden`, which paint() sets true itself,
       so it was true from the first paint on every page and the comment claiming
       it "covers the shells without having to agree with them" never disagreed
       because it never asked.

       Inside the popup and the drawer the real answer is the overlay shell's own
       [hidden] flag — the only "am I on screen" signal upsell.js publishes. On the
       cart PAGE there is no shell at all and the hand is simply in the document.
       So: the nearest shell decides, and no shell means visible. */
    var shell = null;
    (function () {
      var n = mount.parentNode;
      while (n && n.nodeType === 1) {
        if (n.hasAttribute('data-exon-popup') || n.hasAttribute('data-exon-drawer')) { shell = n; break; }
        n = n.parentNode;
      }
    })();
    /* THREE QUESTIONS, kept apart (BUILD-PLAN.md R10, D6):
       - available: the mount is drawn (`!mount.hidden`) — drawing() answers it
         for the ticket, and a folded hand is still available;
       - shown(): available on a surface that is on screen. This is what the
         campaign clock needs, and it holds while the row is FOLDED, so the
         folded line keeps counting down;
       - visible(): somebody can actually see the hand — shown AND the row
         open. This is what the opening, the measurement and the announcement
         ask. */
    function shown() {
      if (mount.hidden) return false;
      return shell ? !shell.hidden : true;
    }
    function visible() {
      return shown() && !folded();
    }

    /* THE POPUP TEASES. Opening the hand there drops the visible cross-sell
       offers from 2.0 to 0.7 (master section 7's own measurement), so the
       control points at the cart instead — EXCEPT in the popup's own
       [data-no-offers] mode, where there is no cross-sell to protect and the
       hand may own the popup. */
    function teasing() {
      if (surface !== 'popup') return false;
      return !(mount.closest && mount.closest('[data-no-offers]'));
    }
    function openCart() {
      try {
        document.dispatchEvent(new CustomEvent('exon:drawer:open'));
      } catch (e) { /* the drawer is optional */ }
    }
    /* THE CUSTOMER OPENS THE HAND: commit first, and only an open that took
       deals. A refused open (another row of the surface would not close)
       changed nothing, so it must not spend an attempt. */
    function openRequest() {
      /* the tease is decided at runtime ([data-no-offers]); a tap that got
         here while the row's go mark was a paint behind still leads to the
         cart, never into a hand the popup must not open */
      if (teasing()) { openCart(); return; }
      var f = fold();
      if (!f || !row || f.open(row) !== true) { repaintOne(); return; }
      var w = writer();
      var d = reader();
      var choice = d ? d.getChoice() : null;
      /* THE HAND IS DEALT THROUGH THE REAL CALLER, on the customer's own
         action, and only when there is not already one open. A deal is never
         issued automatically and never retried: `deal_mystery_hand` answers
         `replay` for a repeated request id, and this surface does not make a
         second one on its own. A LOCKED hand still opens (the customer sees
         what unlocks it); it only never deals (EXO-4824 F3). */
      var atTap = viewNow(current(Date.now()));
      var lockedNow = !!(atTap && atTap.view && atTap.view.locked);
      if (w && choice && !choice.attempt && !writeInFlight && !lockedNow) w.dealMysteryHand();
      repaintOne();
    }

    /* THE ROW ASKS (`xf:toggle`, fold.js): a customer tap either way, or the
       surface's group closing this row because another one is opening. The
       group close is SYNCHRONOUS — the controller reads this row's state the
       moment dispatchEvent returns — so the close path awaits nothing,
       requests nothing and deals nothing. Its ONE refusal is a pick being
       saved (`writeInFlight`): folding then would hide the customer's own
       choice mid-write. A refused tap says so in words; a refused group close
       leaves the other row closed, which is the controller's answer, not
       ours. */
    function onRowToggle(event) {
      if (event.target !== row) return;
      var want = event.detail ? event.detail.want : undefined;
      var reason = event.detail ? event.detail.reason : '';
      if (want === true) { openRequest(); return; }
      if (want !== false) return;
      if (writeInFlight) {
        if (reason === 'tap') announceWord('held');
        return;
      }
      var f = fold();
      if (f) f.close(row);
      repaintOne();
    }

    /* THE ROW LEADS ELSEWHERE (`xf:go`): the popup's tease. Its tap opens the
       cart and never the hand. A go mark one paint stale (the popup just
       entered its own [data-no-offers] mode) is an ordinary open request. */
    function onRowGo(event) {
      if (event.target !== row) return;
      if (teasing()) { openCart(); return; }
      openRequest();
    }

    /* THE ROW OPENED (`xf:open`, after the controller wrote the row open and
       un-inerted its panel). The hand was not on screen until now, so
       everything that waits for somebody to look happens here, through the
       same path a shell opening takes: release the staging that was written
       with transitions held off, measure the stage, repaint (which plays the
       opening, or lands the already-played hand, the receipt or the reduced
       hand), and announce the latest truth. The sealed line is not
       announced: the customer just tapped it. */
    function onRowOpen(event) {
      if (event.target !== row) return;
      settleStill();
      remeasure();
      repaintOne();
      var st = p.getAttribute('data-dmc-state') || 'live';
      if (st !== 'deal' && st !== 'tease') say(st);
    }

    /* THE ROW FOLDED (`xf:close`). The hand leaves the screen: any opening
       stops at once, ANIMATION timers only. The pick's watchdog, held and
       recovery timers belong to the write, which is why a fold is refused
       while one is in flight, and there is no paint(null) teardown here: the
       hand is still available. */
    var tearing = false;   /* api.paint is closing the row for a teardown */
    function onRowClose(event) {
      if (event.target !== row) return;
      clearAnim();
      clearPointer();
      if (!tearing) repaintOne();
    }

    /* the one-moment word, in the live region, from the shell's own copy */
    function announceWord(name) {
      if (!visible()) return;
      var live = announcer(p.querySelector('[data-dmc-live]'));
      var n = words ? words.querySelector('.dmc-s-' + name) : null;
      var t = n ? String(n.textContent || '').trim() : '';
      if (live && t) setText(live, t);
    }

    /* the whole view for this panel only: the same resolution the page-wide
       repaint makes, applied to this one mount */
    function repaintOne() {
      var now = Date.now();
      var res = resolveView(now);
      if (!res.v) report(res.r.reason, now);
      api.paint(res.v);
      retime(res.v);
    }
    function onAttach() {
      var d = window.EXON.drops;
      var id = mount.getAttribute('data-dmc-grant');
      if (!d || typeof d.attachChoiceGrant !== 'function' || !id) return;
      d.attachChoiceGrant(id);
    }
    if (row) {
      row.addEventListener('xf:toggle', onRowToggle);
      row.addEventListener('xf:go', onRowGo);
      row.addEventListener('xf:open', onRowOpen);
      row.addEventListener('xf:close', onRowClose);
    }
    if (attachBtn) attachBtn.addEventListener('click', onAttach);

    var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    function set(el, o) { for (var k in o) el.style.setProperty('--' + k, o[k]); }
    /* TWO KINDS OF TIMER, and only one of them may be cancelled by a fold.
       `anims` are the choreography's beats (the opening chain, the fan's
       stagger, the neighbour knocks); `timers` are everything the WRITE owns:
       the pick's watchdog, the held cue, the recovery and the collapse that
       follow the answer. clear() is the old everything: a pick taking the
       hand, a new deck replacing it, availability tearing it down.
       clearAnim() is all a fold or an opening (play, land, skip) may do, so
       a refused pick still recovers across a fold and reopen (EXO-4824 F1). */
    var anims = [];
    function clear() {
      for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
      timers = [];
      clearAnim();
    }
    function clearAnim() {
      for (var j = 0; j < anims.length; j++) clearTimeout(anims[j]);
      anims = [];
    }
    /* our own motion rides K; anything measured against the network does not.
       Moving the default off 1x is what exposed the difference — at 1x the
       scaling was the identity, so a threshold labelled 460ms had been
       waiting 708. */
    function at(ms, fn) { timers.push(setTimeout(fn, ms * K)); }
    function atAnim(ms, fn) { anims.push(setTimeout(fn, ms * K)); }
    function atReal(ms, fn) { timers.push(setTimeout(fn, ms)); }
    /* RESTAGE WITH TRANSITIONS OFF. Folded, the forced layout read that makes
       "off" take effect is not made: the class is held, and settleStill()
       releases it with its one read when the row opens, so the staged
       positions are reached instantly rather than animated into view. */
    var stillHeld = false;
    function still(fn) {
      p.classList.add('no-tr');
      fn();
      if (folded()) { stillHeld = true; return; }
      void p.offsetWidth;
      p.classList.remove('no-tr');
      stillHeld = false;
    }
    function settleStill() {
      if (!stillHeld) return;
      stillHeld = false;
      void p.offsetWidth;
      p.classList.remove('no-tr');
    }
    function busy(on) { p.classList.toggle('busy', !!on); }
    function lift(c) { return c.querySelector('.dmc-lift'); }
    function clearPointer() {
      for (var i = 0; i < cards.length; i++) {
        var l = lift(cards[i]);
        if (!l) continue;
        l.style.removeProperty('--nx');
        l.style.removeProperty('--nr');
      }
    }

    /* The panel says ONE thing at a time, and it says it in four places: the
       headline, the sub-line, the tag and the receipt all switch together off
       this attribute (drops.css). Announcing it re-reads the copy that is now
       visible, so the screen-reader text can never drift from the words on
       screen — there is only one copy of every sentence, in Liquid. */
    function say(name) {
      p.setAttribute('data-dmc-state', name);
      /* the folded line follows the state however the state was reached — a
         paint, or a write's own timer (held, failed, confirmed) */
      syncRow();
      if (!visible()) return;
      var live = announcer(p.querySelector('[data-dmc-live]'));
      if (!live) return;
      /* THE WON STATE MUST NAME THE PRIZE. The header alone gives a screen-reader
         user "Your card is open · Added automatically at checkout" and never tells
         them WHICH card they won — the one fact this whole surface exists to
         deliver. The prize lives in the receipt, so the won state reads both. */
      var scope = name === 'won' ? '.dmc-h, .dmc-mini' : '.dmc-h';
      var parts = [];
      var roots = p.querySelectorAll(scope);
      for (var r = 0; r < roots.length; r++) {
        var vis = roots[r].querySelectorAll('.dmc-s-' + name);
        for (var i = 0; i < vis.length; i++) {
          var n = vis[i];
          /* skip what the sighted customer is not being shown either: a hidden
             clock span would otherwise announce "Closes in" for a campaign that
             has no clock, and the aria-hidden separator would be read as content */
          if (n.hidden || n.closest('[hidden], [aria-hidden="true"]')) continue;
          var t = (n.textContent || '').replace(/\s+/g, ' ').trim();
          if (t) parts.push(t);
        }
      }
      setText(live, parts.join(' · '));
    }

    /* ── the row's copy, all of it from the shell ──────────────
       THE CURRENT STATE'S TITLE SPAN, chosen the way drops.css chooses it
       (`tease` shows the sealed line's title): one span, never the
       concatenated text of spans that are mutually exclusive on screen. */
    function spanText(group, name) {
      var n = group ? group.querySelector('.dmc-s-' + name) : null;
      return n ? String(n.textContent || '').trim() : '';
    }
    /* THE LOCKED SENTENCE, from the shell's copy template for the shortfall
       the panel shows (the amount short, or only the items short), filled the
       way drops-anchor.js fills its row copy: the words escaped, the one
       number its own bidi-isolated run. Empty when the template or the number
       is not there, so the title falls back to the state's own line. */
    function lockedLine() {
      var amountLine = p.querySelector('[data-dmc-lock-amount-line]');
      var items = !amountLine || amountLine.hidden;
      var line = items ? p.querySelector('[data-dmc-lock-items-line]') : amountLine;
      if (!line || line.hidden) return '';
      var tpl = p.getAttribute(items ? 'data-dmc-copy-row-items' : 'data-dmc-copy-row-amount');
      var numNode = p.querySelector(items ? '[data-dmc-lock-items]' : '[data-dmc-lock-amount]');
      var num = numNode ? String(numNode.textContent || '').trim() : '';
      if (!tpl || !num) return '';
      return esc(tpl).split(items ? '{items}' : '{amount}').join('<span class="ltr">' + esc(num) + '</span>');
    }

    /* THE FOLDED LINE (BUILD-PLAN.md R8), from the panel as it now stands:
       - title: the state's line; LOCKED names the shortfall in the sentence
         the shell carries for it; WON names the prize;
       - state: the CLOCK while the sealed hand or the pick has a deadline to
         state, else the state's word chip;
       - icon: the lock while locked, the check once the draw is recorded.
       No `auto`, ever: a hand being dealt is not news, and a pick neither
       folds nor opens the row. */
    function rowModel() {
      var st = p.getAttribute('data-dmc-state') || 'deal';
      var key = st === 'tease' ? 'deal' : st;
      var m = {
        hidden: false, tone: 'amber', bar: null, icon: 'gift',
        status: ROW_STATUS[st] || 'idle'
      };
      var line = st === 'locked' ? lockedLine() : '';
      /* a won hand names its prize, as the Anchor's chosen row names its
         gift: its own <bdi>, the one part of the line that shrinks */
      var prizeNode = st === 'won' ? p.querySelector('[data-dmc-prize]') : null;
      var prize = prizeNode ? String(prizeNode.textContent || '').trim() : '';
      if (line) m.titleHtml = line;
      else if (prize) m.titleHtml = '<bdi class="xf-rw">' + esc(prize) + '</bdi>';
      else m.title = spanText(titles, key);

      var cd = p.querySelector('[data-dmc-cd]');
      var when = p.querySelector('[data-dmc-when]');
      var left = (CLOCK_STATES[st] && cd && when && !when.hidden) ? String(cd.textContent || '').trim() : '';
      if (left) {
        m.state = { kind: 'num', html: '<span class="ltr">' + esc(left) + '</span>' };
      } else {
        var w = spanText(words, key);
        m.state = w ? { kind: 'word', text: w } : null;
      }

      if (st === 'locked') m.icon = LOCK_ICON;
      if (st === 'won' || st === 'blank') m.icon = 'check';
      return m;
    }

    function syncRow() {
      var f = fold();
      if (!row || !f) return;
      if (mount.hidden) {
        /* keep in step with drops-anchor.js syncRow (EXO-4824 D-1) */
        /* GONE WHILE OPEN: fold first, which hands any focus inside the hand
           back to the row, then hide. Folding is never how availability is
           written, and availability is never written by folding. */
        if (rowOpen()) f.close(row);
        f.set(row, { hidden: true });
        return;
      }
      var m = rowModel();
      /* THE POPUP TEASE IS "LEADS ELSEWHERE": the row's own go mark, written
         here because ExonFold.set has no key for it (BUILD-PLAN.md R5, D4).
         A row that starts teasing while open folds — unless a pick is being
         written, which is never folded under its customer. */
      var tease = teasing();
      if (rowBtn) {
        if (tease && !rowBtn.hasAttribute('data-xf-go')) rowBtn.setAttribute('data-xf-go', '');
        if (!tease && rowBtn.hasAttribute('data-xf-go')) rowBtn.removeAttribute('data-xf-go');
      }
      if (tease && rowOpen() && !writeInFlight) f.close(row);
      f.set(row, m);
      /* keep in step with drops-anchor.js syncRow (EXO-4824 D-1) */
      /* A TICKING CLOCK IS NOT THE ROW'S NAME. fold.js leaves the state slot
         in the button's accessible name (BUILD-PLAN.md D3), so while it
         carries the seconds it is hidden from assistive tech here; the hand
         announces through its own live region. */
      if (rowState) {
        var ticking = !!(m.state && m.state.kind === 'num');
        if (ticking && rowState.getAttribute('aria-hidden') !== 'true') rowState.setAttribute('aria-hidden', 'true');
        if (!ticking && rowState.hasAttribute('aria-hidden')) rowState.removeAttribute('aria-hidden');
      }
    }

    /* ── geometry ──────────────────────────────────────────────
       Derived from the deck's real size rather than pinned at five. The
       approved hand IS five cards and every value below is the approved one
       at n=5; a campaign that deals a different number gets the same hand,
       centred on its own middle, instead of a hand drawn off the panel. */
    function geometry(n, small, avail) {
      var mid = (n - 1) / 2;
      var deal = small ? 62 : 74;
      /* five cards at 74px fill the approved panel; a bigger deck tightens the
         deal so the widest card stays on the felt */
      if (n > 5) deal = Math.max(small ? 34 : 42, Math.round(deal * 5 / n));

      /* THE DEAL IS FITTED TO THE PANEL IT PLAYS IN, MEASURED.
         The sealed fan rotates about --pivot and stays narrow, but the dealt arc
         is the one beat drawn WIDER than the fan — at the approved spacing the
         outermost card sits ±148px from centre plus half a card, which overflows
         a 390px phone inside the popup's gutters. Both .dmcp and .dmcs are
         overflow:hidden, so the end cards were simply clipped for the whole
         reveal — on the mobile-first surface, in the beat the owner approved.
         The approved spacing is kept wherever it fits and only tightened where it
         would be cut off, so the desktop hand is untouched. */
      var cw = small ? 82 : 94;
      if (mid > 0 && avail > 0) {
        var room = (avail / 2) - (cw / 2) - 6;
        if (room > 0) deal = Math.max(26, Math.min(deal, room / mid));
      }

      return {
        mid: mid,
        deal: deal,
        arc: small ? 3.2 : 3.4,
        bow: small ? 3 : 4,
        pile: small ? 30 : 36,
        step: n > 5 ? Math.max(7, Math.round(12 * 5 / n)) : 12
      };
    }

    /* ── the positional states ─────────────────────────────────
       Transform and opacity only. No width, height, top or left moves in any
       beat — this runs on mid-range Android phones. */
    function offTable(d) {
      cards.forEach(function (c, i) {
        var slot = seq.indexOf(i);
        set(c, {
          x: '0px', y: '70px', rot: '0deg', sc: 0.82, o: 0, ly: '0px', oy: '50%',
          z: cards.length * 2 - slot, d: d + 'ms', od: (d * 0.7) + 'ms'
        });
      });
    }
    /* the dealt arc, face up. Slot 0 is rightmost — and it is rightmost in EVERY
       locale, because transforms are physical and do not mirror under
       direction:rtl. It reads as dealing with the copy in Hebrew and against it in
       English; the arc is symmetrical either way, so this is a note rather than a
       defect. Everything else in this surface uses logical properties and does
       mirror. */
    function dealt(d) {
      cards.forEach(function (c, i) {
        var slot = seq.indexOf(i);
        set(c, {
          x: ((cfg.mid - slot) * cfg.deal) + 'px',
          y: (Math.abs(slot - cfg.mid) * cfg.bow) + 'px',
          rot: ((slot - cfg.mid) * cfg.arc) + 'deg',
          sc: 1, o: 1, ly: '0px', oy: '50%',
          z: cards.length * 2 + slot, d: d + 'ms', od: (d * 0.7) + 'ms'
        });
      });
    }
    /* the squared stack — the thing that is turned, and then worked */
    function stack(d) {
      cards.forEach(function (c, i) {
        var slot = seq.indexOf(i);
        set(c, {
          x: '0px', y: (slot * 0.6) + 'px', rot: '0deg', sc: 1, o: 1, ly: '0px', oy: '50%',
          z: cards.length * 2 - slot, d: d + 'ms', od: (d * 0.7) + 'ms'
        });
      });
    }
    /* the approved sealed fan */
    function fan(d, stagger) {
      cards.forEach(function (c, i) {
        var slot = seq.indexOf(i);
        set(c, { oy: 'var(--pivot)' });
        var go = function () {
          set(c, {
            x: '0px', y: '0px', rot: ((slot - cfg.mid) * cfg.step) + 'deg',
            sc: 1, o: 1, ly: '0px', z: slot + 1, d: d + 'ms', od: (d * 0.7) + 'ms'
          });
        };
        if (stagger) atAnim(slot * stagger, go); else go();
      });
    }
    function ry(v) { cards.forEach(function (c) { c.style.setProperty('--ry', v + 'deg'); }); }
    function ground(on) { set(p, { felt: on }); }
    /* an instant landing leaves --d at 0; hover needs a duration back */
    function restoreD() {
      cards.forEach(function (c) { set(c, { d: '380ms', od: '260ms', fd: '560ms' }); });
    }

    /* ── SEALING THE HAND: the moment the invariant is actually enforced ──
       THE FACES COME OFF THE CARDS WHEN THE CARDS TURN OVER, and the slot ids
       go on at the same instant. Never both at once, and that is the point.

       The opening deals the campaign's prize SET face up; the customer then
       picks a face-down card. If a card node kept its face while carrying the
       slot id it posts, the DOM would state a pairing — "this card is EA FC
       26" — that no server ever promised. It would be arbitrary, consistent,
       and inspectable, so a curious customer could read it, believe it, and be
       wrong. The architect's rule that the reveal IS the confirmation would
       hold on the wire and be broken in the document.

       So: face up, no slot id. Face down, no face. The picked card's front is
       written from THE SERVER'S ANSWER, one frame before it turns — which is
       the only moment this client is entitled to name a prize at all.

       Slot ids are assigned by position after the shuffle. They are opaque and
       interchangeable before a pick, so which card gets which is meaningless
       by construction — as it must be. */
    function seal() {
      /* THE PICK BUTTONS ARE NEW NODES. The cards that showed faces are destroyed
         here and replaced, and this is the third and last attempt at this
         invariant — the first two both left a residue, in the same way, for the
         same reason.

         v1 bound prize i to slot i outright. v2 stripped the faces and the data-i
         index, which removed everything a card SAYS — and left everything a card
         IS. A node is its own identifier: the same element survived reveal, seal
         and shuffle, so anything that watched the opening (devtools, an extension,
         a MutationObserver) could hold a reference to the node that showed a given
         prize and read the slot it later submits. Erasing the label off an object
         does not make it a different object, and while a hand is staged unplayed
         inside a closed overlay it sits there indefinitely, at leisure.

         What that pairing actually leaks is nothing — `slots` carry no prize data
         and the server's mapping is independent of our presentation order, so
         "node 3 showed FC 26 and submits slot X" says nothing true about slot X.
         But it is arbitrary, consistent and inspectable, which is precisely the
         shape a customer would read as authoritative and feel cheated by. The
         shuffle exists to break exactly this link for someone watching the screen;
         node identity was quietly reinstating it for anyone watching the DOM.

         So: fresh clones carrying the current visual state, and the array is
         re-ordered too, so array position stops tracking prize order as well. The
         moment is chosen — the stack is squared and face-down, every card overlaps
         every other, and the swap is invisible. */
      var stale = cards;
      var fresh = stale.map(function (o) {
        var n = tpl.content.firstElementChild.cloneNode(true);
        /* the visual state travels, the identity does not */
        n.style.cssText = o.style.cssText;
        n.className = o.className;
        bindCard(n);
        if (o.parentNode) o.parentNode.replaceChild(n, o);
        return n;
      });

      /* perm[newIndex] = oldIndex — a real shuffle of the ARRAY, so even the
         array's own order no longer follows the deal */
      var perm = fresh.map(function (_, i) { return i; });
      for (var i = perm.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var t = perm[i]; perm[i] = perm[j]; perm[j] = t;
      }
      var inv = [];
      perm.forEach(function (oldI, newI) { inv[oldI] = newI; });

      cards = perm.map(function (oldI) { return fresh[oldI]; });
      /* seq maps stage POSITION → card index, so it is remapped rather than
         reset: every card must stay exactly where it is on the felt */
      seq = seq.map(function (oldI) { return inv[oldI]; });

      slotOf = [];
      cards.forEach(function (c, n) { slotOf[n] = slotIds[seq.indexOf(n)]; });
    }

    /* nothing is stamped on a card, so DOM order is the only identity there is —
       and after seal() that order is a shuffle of the deal, not the deal */
    function idx(c) {
      var n = cards.indexOf(c);
      return n < 0 ? 0 : n;
    }

    /* TAKING THE SPENT HAND OUT OF REACH — all of it, not just the pointer.
       `pointer-events: none` and the .min clip both hide these cards from a mouse
       and from the eye, and NEITHER removes them from the accessibility tree or
       the tab order. Without this a keyboard user tabs through four or five
       invisible buttons still announcing "Card · pick this one" on a hand that has
       already been dealt out. `disabled` is also the second lock on the one-pick
       rule: the tap cannot even reach pick(). */
    function retire(except) {
      cards.forEach(function (o) {
        o.style.pointerEvents = 'none';
        if (o !== except) o.disabled = true;
      });
    }

    /* the one place a prize name may be written onto a card: with the server's
       answer in hand, on the card the server answered about */
    function face(c, res) {
      if (!res) return;
      setText(c.querySelector('[data-dmc-name]'), res.name || '');
      setText(c.querySelector('[data-dmc-sub]'), res.sub || '');
      var cover = coverValue(res.art_url);
      var cov = c.querySelector('[data-dmc-cov]');
      if (cov && cover) cov.style.setProperty('--cov', cover);
      var ac = accentValue(res.accent);
      if (ac) c.style.setProperty('--accent', ac);
    }

    /* THE HAND REACTS AS A HAND. Hovering one card pushes its neighbours away
       and tilts them, so the lift reads as a card being drawn OUT of something
       rather than one tile animating alone. Slot-based, not DOM-based, because
       the shuffle has already changed which card sits where. */
    function spreadFrom(c) {
      if (folded() || picked || committing || p.dataset.beat !== 'spread') return;
      var s = seq.indexOf(idx(c));
      cards.forEach(function (o) {
        if (o === c) return;
        var t = seq.indexOf(idx(o));
        var d = t - s;
        var m = Math.abs(d) === 1 ? 7 : 3.5;
        var l = lift(o);
        if (!l) return;
        l.style.setProperty('--nx', (d > 0 ? -m : m) + 'px');
        l.style.setProperty('--nr', (d > 0 ? -1.1 : 1.1) + 'deg');
      });
    }

    /* ── entry states, applied instantly when a beat starts ──── */
    var ENTRY = {
      reveal: function () { resetSeq(); ry(0); ground(0); offTable(0); },
      flip: function () { resetSeq(); ry(0); ground(0); dealt(0); },
      shuffle: function () { ry(180); ground(0); stack(0); },
      spread: function () { ry(180); ground(0); stack(0); }
    };
    function resetSeq() {
      seq = cards.map(function (_, i) { return i; });
    }

    /* ── the four beats ────────────────────────────────────────
       Each returns its own measured length, and the chain starts the next one
       from it — so the four are one continuous move rather than four clips. */
    var RUN = {
      /* 1 · REVEAL — dealt out one at a time, FACE UP. Each card lands in
         front of the ones already down and a little larger, holds its moment,
         then eases back as the next arrives. */
      reveal: function () {
        var ST = 118, FLY = 178, HOLD = 190, EASE = 125;
        var KNOCK = 'cubic-bezier(.18,1.25,.5,1)';
        cards.forEach(function (c, i) {
          var slot = seq.indexOf(i);
          atAnim(slot * ST, function () {
            /* a knock curve: a dealt card overshoots a hair and settles, it
               does not glide to a stop */
            set(c, {
              x: ((cfg.mid - slot) * cfg.deal) + 'px',
              y: (Math.abs(slot - cfg.mid) * cfg.bow - 4) + 'px',
              rot: ((slot - cfg.mid) * cfg.arc) + 'deg',
              sc: 1.16, o: 1, z: 40 + slot, d: FLY + 'ms', od: '120ms', ec: KNOCK
            });
          });
          /* cards arrive one at a time, so each one can KNOCK THE LAST: the
             card already down takes a small shove as the next lands on its
             edge, and recovers. */
          if (slot > 0) {
            atAnim(slot * ST + FLY - 26, function () {
              var prev = lift(cards[seq[slot - 1]]);
              if (!prev) return;
              prev.style.setProperty('--nx', '5px');
              prev.style.setProperty('--nr', '0.9deg');
            });
            atAnim(slot * ST + FLY + 130, function () {
              var prev = lift(cards[seq[slot - 1]]);
              if (!prev) return;
              prev.style.removeProperty('--nx');
              prev.style.removeProperty('--nr');
            });
          }
          atAnim(slot * ST + HOLD, function () {
            set(c, {
              y: (Math.abs(slot - cfg.mid) * cfg.bow) + 'px', sc: 1, z: cards.length * 2 + slot,
              d: EASE + 'ms', ec: 'var(--uease)'
            });
          });
        });
        return (cards.length - 1) * ST + HOLD + EASE;
      },

      /* 2 · FLIP — they gather into a squared stack and the STACK turns as
         one: identical timing on every card, no stagger, so it reads as one
         object turning rather than n cards flipping. */
      flip: function () {
        var GATHER = 165, TURN = 185;
        atAnim(0, function () { stack(GATHER); });
        atAnim(GATHER + 10, function () {
          cards.forEach(function (c) {
            c.style.setProperty('--fd', TURN + 'ms');
            c.style.setProperty('--ry', '180deg');
          });
        });
        atAnim(GATHER + 10 + TURN * 0.55, function () {
          cards.forEach(function (c) { c.classList.add('glint'); });
        });
        atAnim(GATHER + 10 + TURN + 400, function () {
          cards.forEach(function (c) { c.classList.remove('glint'); });
        });
        /* the faces come off as the turn completes — past 90° the backface is
           already hiding them, so this changes nothing anyone can see and
           everything about what the document claims */
        atAnim(GATHER + 10 + TURN, seal);
        return GATHER + TURN + 20;
      },

      /* 3 · SHUFFLE — a true OVERHAND, and the reason it is overhand rather
         than a repeated cut is worth stating: a cut only ROTATES a deck, so
         three cuts compose into one cut and nothing is mixed. An overhand
         moves packets off the top of the hand onto a growing second pile,
         which reverses them at packet level — a real permutation. `seq` is
         genuinely rewritten, which is why the hand is not in the order it was
         dealt, and why the neighbour push has to be slot-based.

         This is choreography, not a claim about odds: WHICH slot holds which
         prize is the server's business and this island never learns it. */
      shuffle: function () {
        var D = cfg.pile, MOVE = 104, t = 80;
        var src = seq.slice(), out = [];
        /* packets sized off the real deck: roughly two, two, and the rest */
        var TAKE = [];
        var left = src.length;
        while (left > 0) {
          var take = left <= 2 ? left : Math.min(2, left - 1);
          TAKE.push(take);
          left -= take;
        }

        /* z derived from the deck like every other positional state, not pinned at
           10/30: a deck past ten cards drove these negative and dropped cards
           behind the felt (z-index 0), and geometry() explicitly supports n > 5 */
        var ZL = cards.length * 2;
        var ZH = cards.length * 4;

        atAnim(0, function () {
          src.forEach(function (ci, n) {
            set(cards[ci], { x: D + 'px', y: (n * 0.6) + 'px', rot: '-2.5deg', sc: 1, z: ZL - n, d: '70ms' });
          });
        });

        TAKE.forEach(function (take) {
          out = src.slice(0, take).concat(out);   /* the packet lands on TOP */
          src = src.slice(take);
          (function (o, s, tt) {
            atAnim(tt, function () {
              o.forEach(function (ci, n) {
                set(cards[ci], { x: (-D) + 'px', y: (n * 0.6) + 'px', rot: '2.5deg', z: ZH - n, d: MOVE + 'ms' });
              });
              s.forEach(function (ci, n) {
                set(cards[ci], { x: D + 'px', y: (n * 0.6) + 'px', rot: '-2.5deg', z: ZL - n, d: MOVE + 'ms' });
              });
            });
          })(out.slice(), src.slice(), t);
          t += MOVE + 8;
        });

        seq = out;
        atAnim(t, function () { stack(95); });        /* squared on the table */
        return t + 110;
      },

      /* 4 · SPREAD — the approved sealed fan, and the surface goes live. */
      spread: function () {
        var ST = 20, D = 230;
        cards.forEach(function (c) { set(c, { ec: 'cubic-bezier(.22,1.14,.42,1)' }); });
        atAnim(0, function () { fan(D, ST); });
        atAnim(D + cards.length * ST + 30, function () {
          cards.forEach(function (c) { set(c, { ec: 'var(--uease)' }); });
        });
        atAnim(70, function () { ground(1); });
        return cards.length * ST + D + 20;
      }
    };

    /* ── THE PICK, AND THE WRITE BEHIND IT ─────────────────────
       The pick decides which product the customer actually receives, so the
       card CANNOT turn on click. It flies to centre and stays face-down until
       the server answers. The reveal IS the confirmation.

       Three outcomes, and the surface is honest in all three:
         confirmed  → the card turns, green arrives, the receipt lands
         still out  → the HELD state, appearing only once the wait has
                      outlasted the flight, so a fast confirmation never
                      shows a waiting state to anybody
         never landed → the card never turned, so nothing false was promised;
                      the hand comes back and is pickable again. Recovery is
                      the surface returning, not a new screen.
       ──────────────────────────────────────────────────────── */
    function pick(c) {
      /* a folded hand is inert (the controller un-inerts it only when the row
         opens), so this only refuses a stale or programmatic call */
      if (folded() || locked || picked || committing || p.dataset.beat !== 'spread') return;
      /* A PICK ANYWHERE IS A PICK EVERYWHERE. `picked` above is this panel's; this
         is the page's. The popup and the drawer are both mounted on every page, so
         without a shared flag a customer who picks in one still has a live, spread,
         fully pickable hand waiting in the other — and if the server correctly
         answers already-picked to the second tap, the card they just touched turns
         over showing the FIRST pick's prize. The wire contract holds and the
         surface lies, which is the exact failure the reveal-is-the-confirmation
         rule exists to prevent. */
      if (writeInFlight) return;
      var w = writer();
      /* the guard is real rather than defensive: boot() refuses to render a
         pickable hand without a write path, so reaching here means the path
         disappeared between paint and tap */
      if (!w) { fault('mystery_write_unavailable', ''); return; }

      /* RESOLVED BEFORE ANYTHING MOVES. The old version tore the hand apart first
         and discovered the missing id afterwards, leaving an empty stage with no
         way back and no absence either — a defect path that still owed the
         customer a state. Bailing here costs one ignored tap and nothing else. */
      var slotId = slotOf[idx(c)];
      if (slotId === undefined || slotId === null) {
        fault('mystery_slot_unassigned', '');
        return;
      }

      /* THE OPENING'S TRAILING TIMERS MUST NOT LAND ON THE FLIGHT. The hand becomes
         pickable at the START of the spread beat, while the fan is still settling,
         so the chain's terminal timer is still pending — and it calls restoreD(),
         which rewrites --d and --fd on EVERY card including the one now flying to
         centre, re-interpolating a running transition and dropping will-change out
         from under it. skip(), land() and landPicked() all clear first; this was
         the one path that did not. */
      clear();
      /* ...and THE PICK ENDS THE OPENING. The write owns the hand from here, so
         nothing may treat it as an opening still to finish: a fold would
         restage it and the reopen would replay it over the write's own
         recovery (EXO-4824 F1). */
      opening = false;

      committing = true;
      writeInFlight = true;
      busy(true);
      clearPointer();
      p.classList.add('committing');
      c.setAttribute('aria-pressed', 'true');

      /* THE SPLIT, and it is the whole reason this reads instant AND honest:
         the MOTION answers the tap, the ANSWER waits for the server. The
         siblings sink now and the receipt opens now — gray, loading, naming
         NOTHING. Only "added to your order" is held back, because only that
         part is the server's to say. */
      /* The HEADER does not change on the tap and the state attribute stays
         `live`: the customer has not been told anything new yet, and a header
         that changed here would be narrating our own animation. What changes
         is the receipt, which opens in its pending look — that is CSS on
         .summary:not(.confirmed), so the gray strip and its "saving your
         choice" line need no state of their own. */
      p.classList.add('summary');

      /* the flight. The origin animates back to the card's own centre as it
         goes, so scale() grows it in place instead of throwing it up into the
         header. It arrives FACE DOWN. */
      c.style.setProperty('--fd', '620ms');
      set(c, { oy: '50%', x: '0px', y: '0px', rot: '0deg', ly: '-2px', sc: 1.14, z: 30, d: FLIGHT + 'ms' });
      cards.forEach(function (o) {
        if (o === c) return;
        var slot = seq.indexOf(idx(o));
        set(o, { rot: ((slot - cfg.mid) * cfg.step) + 'deg', ly: '70px', sc: 0.86, o: 0, d: '480ms', od: '380ms' });
        o.style.pointerEvents = 'none';
      });

      /* Real milliseconds, because the network is a real clock — but compared
         against the flight's REAL duration, which is FLIGHT scaled by K. See
         holdAfter(). */
      atReal(holdAfter(), function () {
        if (!committing) return;
        p.classList.add('held');
        say('held');
      });

      var settled = false;

      /* A WRITE THAT NEVER SETTLES. A promise that neither resolves nor rejects
         leaves this panel committing forever: no hand, no receipt, no way back,
         and `busy` pinned so every card holds its GPU layer. Nothing recovers and
         nothing is visibly wrong. So the surface sets its own outer bound and
         treats silence as the failure it is — the card never turned, so this
         costs the customer nothing but the wait they already had. */
      atReal(WRITE_TIMEOUT, function () {
        if (!committing || settled) return;
        settled = true;
        fault('mystery_pick_timeout', slotId);
        failed(c, 'timeout');
      });

      /* THE SERVER'S OWN SLOT INTEGER, SUBMITTED UNCHANGED. `slotId` came out
         of the hand this panel is showing, which is `slot_count` rendered; the
         shuffle above reorders NODES, never the slot each node submits.

         drops.js answers with the choice action it published — never a throw
         for an ordinary refusal, because a stale hand and a blank draw are
         OUTCOMES rather than errors. The four this surface can be handed:

           recorded  the card holds a prize and the grant is recorded
           blank     the card was real and held nothing. Not a failure, not a
                     lost write, and NOT re-dealt on its own.
           stale     the hand, cart or definition moved under the pick; the
                     card never turned, and `/state` already carries the truth
           refused   including outcome_unknown after one reconciliation */
      w.pickMysteryCard(slotId).then(function (action) {
        if (settled) return;
        settled = true;
        if (!action || typeof action !== 'object') { failed(c, 'no_action'); return; }
        if (action.state === 'recorded' || action.state === 'blank') {
          confirmed(c, action);
          return;
        }
        failed(c, action.code || action.state || 'refused');
      }, function (err) {
        if (settled) return;
        settled = true;
        failed(c, err);
      });
    }

    /* the server answered. Only now does anything turn.

       A BLANK IS A REAL ANSWER AND IT TURNS THE CARD TOO. The prototype this
       port comes from had no blanks at all — every card was a prize — and the
       owner's current decision permits a configured deck to hold them. So the
       card still turns, because the reveal IS the confirmation either way; what
       differs is that nothing turns GREEN and the receipt names no prize. A
       blank presented as a failure would tell a member their write was lost
       when it was recorded, and would invite them to spend a second attempt
       recovering from something that already happened. */
    function confirmed(c, action) {
      if (!committing) return;
      committing = false;
      writeInFlight = false;
      picked = true;
      var won = action.state === 'recorded';
      /* the SERVER'S record of what this attempt produced, read back out of
         the state drops.js published with the write's own envelope. The card's
         face is written from this and from nothing else. */
      var settledView = viewNow(current(Date.now()));
      var pick = (settledView && settledView.view && settledView.view.pick)
        ? settledView.view.pick : null;
      var res = won && pick ? { name: pick.name, sub: pick.sub, art_url: '' } : null;
      p.classList.remove('held', 'committing');
      p.classList.add('picked');
      p.classList.toggle('blank', !won);
      /* the front is written HERE, from the SERVER's record of what was won,
         and only then does the card turn. This is the reveal being the
         confirmation, literally. A blank writes no face at all. */
      if (res) {
        face(c, res);
        if (pick && pick.cover) {
          var cov = c.querySelector('[data-dmc-cov]');
          if (cov) cov.style.setProperty('--cov', pick.cover);
        }
      }
      c.style.setProperty('--ry', '360deg');

      /* The prize arrives WITH the green, in the same frame — never before it
         and never after. A receipt filled late leaves the strip still reading
         "saving your choice" while the card is already turning: true a moment
         ago, not true now. The whole surface says one thing at a time. */
      var art = p.querySelector('[data-dmc-art]');
      var cover = res ? coverValue(res.art_url) : '';
      if (art) {
        art.classList.toggle('has-cov', !!cover);
        art.style.setProperty('--cov', cover || 'none');
      }
      setText(p.querySelector('[data-dmc-prize]'), (res && res.name) || '');
      setText(p.querySelector('[data-dmc-prize-sub]'), (res && res.sub) || '');
      say(won ? receiptState(pick) : 'blank');
      p.classList.add('confirmed');
      /* the losing cards are clipped by .min, and clipping is not removal: without
         this they stay in the tab order announcing "pick this one" to a keyboard
         user, on a hand that is already spent */
      retire(c);

      /* THE SIBLINGS READ THE SAME SERVER RECORD. Every choice write answers
         with the projection `/state` would return, and drops.js published it
         before this promise settled — so the other panels are repainted from
         the server's own `last_attempt`, not from a second local memory of
         the pick. */
      syncSiblings();

      /* the stage collapses only once the card has finished turning, so the
         reveal is never cut short by the receipt arriving. The WON card is
         disabled at that same moment and not before: it stays a live element
         while it is the thing on screen, and leaves the tab order when the stage
         that holds it collapses to nothing. Disabling it earlier would drop focus
         mid-reveal for a keyboard user; leaving it enabled after would keep a
         zero-height button in the tab order still offering "pick this one". */
      at(700, function () {
        p.classList.add('min');
        busy(false);
        retire(null);
      });
    }

    /* the write never landed. The card never turned, so no prize was ever
       claimed on screen and nothing has to be taken back — the hand simply
       comes home and is pickable again. */
    function failed(c, err) {
      if (!committing) return;
      committing = false;
      /* the attempt was NOT spent, so the shared in-flight lock lifts with it —
         otherwise one failed write would leave every mount on the page
         permanently unpickable with nothing to show for it. Whether another
         attempt is available is still the server's answer, never this flag. */
      writeInFlight = false;
      p.classList.remove('held', 'summary');
      say('fail');
      fault('mystery_pick_failed', err);
      cards.forEach(function (o) {
        var slot = seq.indexOf(idx(o));
        set(o, {
          oy: 'var(--pivot)', x: '0px', y: '0px', rot: ((slot - cfg.mid) * cfg.step) + 'deg',
          ly: '0px', sc: 1, o: 1, z: slot + 1, d: '440ms', od: '380ms'
        });
      });
      at(470, function () {
        p.classList.remove('committing');
        c.setAttribute('aria-pressed', 'false');
        cards.forEach(function (o) { o.style.pointerEvents = ''; });
        busy(false);
      });
      /* the failure line stands long enough to be read, then the surface is
         just the sealed hand again */
      at(4200, function () {
        if (picked || committing) return;
        say('live');
      });
    }

    /* the escape from the opening: it lands the surface in the sealed spread hand
       rather than advancing a beat */
    function skip() {
      clearAnim();
      opening = false;
      still(function () { ENTRY.spread(); fan(0, 0); ground(1); seal(); });
      p.dataset.beat = 'spread';
      busy(false);
      restoreD();
    }

    /* ── building the deck ─────────────────────────────────────
       Cloned from the Liquid <template>, so this island writes data into
       markup somebody else authored and holds no HTML of its own. */
    function build(v, small) {
      var old = Array.prototype.slice.call(stage.querySelectorAll('.dmc-card'));
      for (var k = 0; k < old.length; k++) old[k].parentNode.removeChild(old[k]);
      cards = [];
      slotIds = v.slots.slice();
      /* Measured off the stage the cards land on — and at BUILD time that
         measurement is usually zero, because the deck is built during the
         page-load paint while the mount and its overlay are still hidden. That is
         not a fallback that quietly works: it is the common case, and an earlier
         comment here claimed a later rebuild would correct it, which was false —
         the signature does not change when a shell opens. So the real measurement
         happens in remeasure(), on the visibility transition, just before the
         opening plays. This call is the floor for the cart page, where the stage
         is laid out from the start. */
      cfg = geometry(v.faces.length, small, folded() ? 0 : (stage.clientWidth || 0));
      narrow = small;

      v.faces.forEach(function (pr) {
        var node = tpl.content.firstElementChild.cloneNode(true);
        if (pr.accent) node.style.setProperty('--accent', pr.accent);
        var cov = node.querySelector('[data-dmc-cov]');
        if (cov && pr.cover) cov.style.setProperty('--cov', pr.cover);
        setText(node.querySelector('[data-dmc-name]'), pr.name);
        setText(node.querySelector('[data-dmc-sub]'), pr.sub);
        bindCard(node);
        stage.appendChild(node);
        cards.push(node);
      });
      resetSeq();
    }

    /* THE MEASUREMENT THAT ACTUALLY COUNTS. Called when the panel becomes visible,
       which for the popup and the drawer is the first moment the stage has a
       width at all. Geometry only — no rebuild, so no faces are re-dealt and no
       node identity is disturbed. */
    function remeasure() {
      if (!cards.length || folded()) return;
      var w = stage.clientWidth || 0;
      if (!w) return;
      cfg = geometry(cards.length, narrow, w);
    }

    /* one place, because seal() builds a second generation of these nodes and a
       listener set that lived only in build() would not survive it */
    function bindCard(node) {
      node.addEventListener('pointerenter', function () { spreadFrom(node); });
      node.addEventListener('focus', function () { spreadFrom(node); });
      node.addEventListener('pointerleave', clearPointer);
      node.addEventListener('blur', clearPointer);
      node.addEventListener('click', function (e) {
        if (folded()) return;
        if (p.dataset.beat !== 'spread') { e.stopPropagation(); skip(); return; }
        pick(node);
      });
    }

    /* A TAP ANYWHERE ON THE STAGE SKIPS THE OPENING — and this really is anywhere,
       now. The approved mockup bound the felt as well as the cards; this port bound
       only the cards while its comment claimed the wider behaviour, so a customer
       who tapped the felt to hurry it along got nothing. Bound once, on the stage,
       rather than per card: the cards are rebuilt on every fresh deck and this
       listener is not. */
    stage.addEventListener('click', function () {
      if (folded()) return;
      if (p.dataset.beat !== 'spread') skip();
    });

    /* the whole opening, chained off each beat's measured length */
    function playAll() {
      clearAnim();
      still(function () { ENTRY.reveal(); });
      p.dataset.beat = 'reveal';
      busy(true);
      opening = true;
      var chain = function (n, delay) {
        if (n >= BEATS.length) {
          atAnim(delay, function () { busy(false); opening = false; restoreD(); });
          return;
        }
        atAnim(delay, function () {
          p.dataset.beat = BEATS[n];
          chain(n + 1, RUN[BEATS[n]]() + 30);
        });
      };
      chain(1, RUN.reveal() + 30);
    }

    /* the sealed hand, with no choreography at all: reduced motion, and any
       repaint that finds the surface already open */
    function land() {
      clearAnim();
      opening = false;
      still(function () { ENTRY.spread(); fan(0, 0); ground(1); seal(); });
      p.dataset.beat = 'spread';
      busy(false);
      restoreD();
    }

    /* a pick this session already made: the receipt, and nothing pickable */
    /* THE RECEIPT SAYS ONE OF THREE THINGS, and which one is entirely the
       server's to decide:
         blank    the draw was recorded and held nothing
         pending  a win whose provider issuance has not landed. It names no
                  code, because there is none to name.
         won      a win with a bound, disclosed benefit */
    function receiptState(pk) {
      if (!pk || pk.outcome !== 'win') return 'blank';
      return pk.code_available ? 'won' : 'pending';
    }

    function landPicked(pk) {
      clear();
      opening = false;
      picked = true;
      committing = false;
      still(function () {
        ENTRY.spread();
        fan(0, 0);
        /* sealed here too, and it matters more here than anywhere: this path
           runs on a RELOAD after the pick, so the faces were never dealt and
           must never appear. The prize the customer won is named once, in the
           receipt, from the server's own record of it. */
        seal();
        retire(null);
      });
      restoreD();
      p.dataset.beat = 'spread';
      var art = p.querySelector('[data-dmc-art]');
      if (art) {
        art.classList.toggle('has-cov', !!pk.cover);
        art.style.setProperty('--cov', pk.cover || 'none');
      }
      setText(p.querySelector('[data-dmc-prize]'), pk.name);
      setText(p.querySelector('[data-dmc-prize-sub]'), pk.sub);
      p.classList.toggle('blank', pk.outcome !== 'win');
      say(receiptState(pk));
      p.classList.add('picked', 'summary', 'confirmed', 'min');
      busy(false);
    }

    /* Repaint is idempotent and cheap when nothing changed: the deck is
       rebuilt only when the campaign's own shape changes, because rebuilding
       it mid-hand would deal a fresh deck under a customer's finger. */
    function paintView(v) {
      if (!v) {
        /* THE WRITE OUTLIVES THE PANEL, AND THE LOCK MUST NOT.
           A campaign expiring mid-write reaches here: clear() cancels the write
           watchdog, committing goes false, and the eventual promise settlement
           then returns immediately from confirmed()/failed() because committing
           is false — so the page-wide lock would stay set forever, and every
           later hand would render fine and silently refuse every tap at the
           in-flight guard. The panel's animation state is this function's to
           tear down; the TRANSACTION is not, so it is released explicitly. */
        if (committing) {
          committing = false;
          writeInFlight = false;
          fault('mystery_pick_abandoned', built.split('|')[0]);
        }
        clear();
        mount.hidden = true;
        picked = false;
        built = '';
        played = '';
        return;
      }

      var wasLocked = locked;
      locked = v.locked === true;
      var sig = v.id + '|' + v.slots.join(',') + '|' + v.faces.length + '|' +
        (v.pick ? (v.pick.outcome + ':' + v.pick.slot) : '');
      var fresh = sig !== built;

      /* THE SERVER CATCHING UP WITH US IS NOT A NEW HAND. After a successful
         pick, the next state refresh carries `pick` for the first time — so
         the signature changes and this would re-deal a fresh deck over the
         receipt the customer is reading. Adopt the signature, change
         nothing: the surface already shows what the server is now telling
         us, because the server is what put it there. */
      if (fresh && picked && v.pick) { built = sig; fresh = false; }

      if (fresh) {
        if (committing) return;   /* a write is out; the deck is not ours to replace */
        /* a new deck supersedes the last write's follow-up timers, which the
           opening entry points no longer cancel */
        clear();
        built = sig;
        /* the popup and the drawer are the narrow hand; the cart page is the
           wide one. Same hand, two sets of measurements — the approved
           mockup drew both. */
        build(v, mount.getAttribute('data-dmc-surface') !== 'cart');
        picked = false;
        p.classList.remove('picked', 'summary', 'confirmed', 'min', 'held', 'committing');
      }

      setText(p.querySelector('[data-dmc-top]'), v.top);
      setText(p.querySelector('[data-dmc-cd]'), v.left);
      show(p.querySelector('[data-dmc-when]'), !!v.left);
      /* THE LOCKED LINE NAMES THE REAL SHORTFALL: the amount while one is
         missing, otherwise the missing items — never "₪0". */
      var itemsOnly = v.locked === true && v.remaining_agorot === 0 &&
        typeof v.remaining_items === 'number' && v.remaining_items > 0;
      setText(p.querySelector('[data-dmc-lock-amount]'), itemsOnly ? '' : money(v.remaining_agorot));
      setText(p.querySelector('[data-dmc-lock-items]'), itemsOnly ? String(v.remaining_items) : '');
      show(p.querySelector('[data-dmc-lock-amount-line]'), !itemsOnly);
      show(p.querySelector('[data-dmc-lock-items-line]'), itemsOnly);
      mount.hidden = false;

      var tease = teasing();
      p.setAttribute('data-dmc-tease', tease ? 'true' : 'false');
      p.setAttribute('data-dmc-open', (!folded() && !tease) ? 'true' : 'false');
      /* 'locked' names both a hand held back and no hand at all; the stage
         rule in drops.css needs to tell them apart */
      p.setAttribute('data-dmc-dealt', v.slots.length ? 'true' : 'false');

      /* THE RECEIPT'S CONTROLS. Only a grant the server says is bound AND
         disclosed to this member gets a button; readiness without disclosure
         is the pending line, never a control that cannot work. */
      if (v.pick) {
        if (v.pick.choice_grant_id) mount.setAttribute('data-dmc-grant', v.pick.choice_grant_id);
        else mount.removeAttribute('data-dmc-grant');
        show(attachBtn, !!v.pick.attachable);
        show(keptNode, v.pick.outcome === 'win' && !v.pick.attachable);
        show(noexpNode, !!v.pick.no_expiry);
      } else {
        mount.removeAttribute('data-dmc-grant');
        show(attachBtn, false);
        show(keptNode, false);
        show(noexpNode, false);
      }

      /* NOTHING DEALT YET: the folded line, and the deal is the tap. There
         is no stage to play into, so the opening's whole chain is skipped
         rather than run against an empty hand. */
      if (!v.slots.length && !v.pick) {
        say(locked ? 'locked' : (tease ? 'tease' : 'deal'));
        return;
      }

      for (var ci = 0; ci < cards.length; ci++) {
        cards[ci].disabled = locked;
        cards[ci].setAttribute('aria-disabled', locked ? 'true' : 'false');
      }
      if (locked) {
        if (fresh) { played = sig; land(); }
        say('locked');
        return;
      }
      if (wasLocked) say('live');

      /* ANNOUNCED AFTER THE ZONES ARE FILLED, NOT BEFORE. say() reads textContent
         at call time, so announcing first produced "Pick one card · Biggest in
         the deck: · Closes in" — the copy without any of the facts, and on a
         rebuild the PREVIOUS hand's facts. */
      if (fresh) say('live');

      /* THE OPENING PLAYS WHEN SOMEBODY IS LOOKING, AND NOT BEFORE.
         `paint()` runs on the drops-state event during page load, which on the
         popup and the drawer is long before either overlay opens. Playing there
         spends the whole approved surface — reveal, flip, shuffle, spread — into
         a hidden shell, and by the time the customer opens it the signature is
         unchanged, so they are handed a pre-spread hand and never see the
         choreography at all. That is two of the three mounts, including the
         mobile-first one.

         So the opening is keyed to VISIBILITY rather than to arrival, and
         `played` remembers which hand was played rather than whether anything
         was: a fresh deck re-arms it, and the shell's own [hidden] observer
         brings us back here the moment it opens. */
      if (v.pick) {
        if (fresh) landPicked(v.pick);
        /* the receipt state was very likely set while this mount was hidden —
           either the customer picked in the other shell, or they closed this one
           mid-write. Either way the announcement was suppressed at the time
           (say() will not speak from a hidden panel), so it is made now, when
           somebody is actually here to receive it. */
        else say(receiptState(v.pick));
        return;
      }
      if (reduced) {
        if (fresh) { played = sig; land(); }
        return;
      }
      if (!visible()) {
        /* A SHELL CAN CLOSE MID-BEAT, and that is not the same as never having
           opened. Without this the opening would finish inside the closed
           overlay — `played` is already set, so re-opening returns early and the
           customer never sees the choreography at all, which is the very defect
           the visibility gate was added to fix, arriving through the back door.
           So a close mid-opening cancels, restages, and forgets it played. A
           ROW folding mid-beat is the same close: it cancels the beats only
           (clearAnim), never the write's own timers, and restages without a
           layout read (still() holds transitions off until the row opens). */
        if (opening && !committing) {
          clearAnim();
          opening = false;
          played = '';
          still(function () { ENTRY.reveal(); });
          busy(false);
          return;
        }
        /* stage it off-table so an opening shell has something to deal FROM,
           and leave it un-played */
        if (fresh) still(function () { ENTRY.reveal(); });
        return;
      }
      if (played === sig) {
        /* it has been played and we are visible again: no re-deal, but the
           announcement belongs to a panel that is actually on screen, and this
           one may have been hidden when its state was last set */
        say(p.getAttribute('data-dmc-state') || 'live');
        return;
      }
      /* the first moment this stage has a real width — see remeasure() */
      remeasure();
      played = sig;
      playAll();
    }

    /* THE FOLDED LINE IS WRITTEN AFTER THE HAND IS PAINTED, whichever way the
       paint returned (a tick on a reduced-motion hand returns early and the
       clock still has to move), and the row is folded before any of it: its
       first write is also when the controller folds a server-open row
       (fold.js ready()), so the disclosure below reads the row as it
       really is. */
    api = {
      el: p,
      paint: function (v) {
        var f = fold();
        if (row && f) f.set(row, {});
        /* keep in step with drops-anchor.js syncRow (EXO-4824 D-1) */
        /* GONE WHILE OPEN: the row folds BEFORE the teardown hides the mount,
           so fold.js still finds the focus inside the panel and hands it to
           the row (EXO-4824 F4). The close's own repaint is skipped: this
           paint is the repaint. */
        if (!v && row && f && rowOpen()) {
          tearing = true;
          try { f.close(row); } finally { tearing = false; }
        }
        paintView(v);
        syncRow();
      },
      /* `isOpen` is what the page-wide loop asks to decide whether the hand is
         on screen; `shown` is what its clock asks, and it holds while folded */
      isOpen: visible,
      shown: shown
    };
    return api;
  }

  /* ------------------------------------------------------------
     the loop
     ------------------------------------------------------------ */
  var panels = [];
  var shells = [];
  var ticker = null;

  /* One second-resolution repaint, alive only while a clock is running AND a hand
     is on a surface that is on screen — each panel answers that against its own
     overlay shell. A FOLDED hand counts: the folded line carries the clock, and
     the row's own state is rewritten on every tick (BUILD-PLAN.md R10, D6). The
     strip already ticks for the page as a whole, and ticking a hidden node is
     work nobody can see. */
  function anyShown() {
    for (var i = 0; i < panels.length; i++) if (panels[i].shown()) return true;
    return false;
  }

  function retime(v) {
    var need = !!(v && v.left) && anyShown();
    if (need && !ticker) ticker = setInterval(repaint, 1000);
    else if (!need && ticker) { clearInterval(ticker); ticker = null; }
  }

  /* ARM `repaint`, NOT A CLOCK-ONLY TICK — and this is the exemplar's shape for a
     reason the first version paid for. A tick that only rewrites the countdown
     never re-asks whether the campaign still exists, and never calls retime(), so
     at expiry compose() drops the item, the tick hits its own null guard and
     returns, and the panel keeps its last countdown text, stays on screen, STAYS
     PICKABLE, and runs its interval for the life of the page. repaint() does
     everything tick did plus the two things that matter: it re-derives, and it
     re-arms. The divergence bought nothing and cost the expiry path. */
  /* ONE LIVE REGION FOR THE WHOLE SURFACE, NOT ONE PER MOUNT.

     The shell carries a role=status node and is rendered up to three times on a
     page, so three live regions exist and a shared state refresh would have all of
     them announcing the same sentence at once. The drops surface speaks with one
     voice: exactly one node keeps role and aria-live, and the others are stripped
     of both. Stripping rather than removing, because the markup is Liquid's and
     this island does not author or delete markup — it only decides which node is
     the one.

     WHICH node is not a fixed choice, and picking one once was wrong: the first
     region in the document sits inside the popup, and a region inside a [hidden]
     overlay is display:none, which assistive tech does not announce at all. So the
     live region is whichever panel is SPEAKING — say() only speaks when its own
     mount is visible — and every other region is stripped as it is handed over. */
  function announcer(node) {
    if (!node) return null;
    var nodes = all('[data-dmc-live]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i] === node) continue;
      nodes[i].removeAttribute('role');
      nodes[i].removeAttribute('aria-live');
      if (nodes[i].textContent) nodes[i].textContent = '';
    }
    if (!node.getAttribute('role')) node.setAttribute('role', 'status');
    if (!node.getAttribute('aria-live')) node.setAttribute('aria-live', 'polite');
    return node;
  }

  /* NO PAGE-LOCAL PICK RECORD, AND THAT IS THE CHANGE THE E3 SEAM BOUGHT.
     Every choice write answers with the SAME projection `/state` would return
     (store.ts envelopeWithResult), and drops.js publishes it before the promise
     settles — so `last_attempt` is already the server's own record by the time
     any panel repaints. A second, client-side memory of the pick would be a
     second answer to the same question, which is the one thing this stack does
     not allow. */
  function viewNow(r) { return r; }

  /* one panel confirmed a pick; the others are holding a hand that is now spent */
  function syncSiblings() {
    var r = viewNow(current(Date.now()));
    for (var i = 0; i < panels.length; i++) panels[i].paint(r.view);
  }

  /* THE VIEW EVERY MOUNT PAINTS, one resolution for the page-wide repaint and
     for a single row answering its own commit */
  function resolveView(now) {
    var r = viewNow(current(now));
    var v = r.view;

    /* A hand nobody can commit is worse than no hand: it takes a choice it
       cannot keep. So the write path is part of whether this surface exists at
       all, not a check performed at tap time. */
    if (v && !v.pick && !writer()) {
      fault('mystery_write_unavailable', v.id);
      v = null;
      r = { view: null, reason: '' };
    }
    return { r: r, v: v };
  }

  function repaint() {
    var now = Date.now();
    var res = resolveView(now);
    var v = res.v;

    if (!v) report(res.r.reason, now);
    for (var i = 0; i < panels.length; i++) panels[i].paint(v);
    retime(v);
    /* the ticket stands down only for a surface that is actually drawing, so
       it is re-asked whenever that answer changes */
    var now2 = drawing();
    if (now2 !== drew) { drew = now2; handOverToTicket(); }
  }
  var drew = false;

  function boot() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;

    var mounts = all('[data-drop-mystery]');
    if (!mounts.length) return;   /* this surface is not on this page */

    /* EVERY LIVE REGION IS DISARMED AT BOOT, and exactly one is armed later by the
       panel that speaks. The Liquid shell renders role=status on each of its up-to-
       three mounts, so leaving them as authored means three live regions exist
       from first paint — and the previous fix only disarmed them the first time a
       VISIBLE panel spoke, which on a page where every mount starts hidden is
       never. Disarming first makes the invariant true from the start rather than
       from the first announcement. */
    var regions = all('[data-dmc-live]');
    for (var r = 0; r < regions.length; r++) {
      regions[r].removeAttribute('role');
      regions[r].removeAttribute('aria-live');
    }

    /* a browser with no <template> support gets no hand, and that is the
       correct outcome rather than a fallback: the card markup lives in the
       template and there is nothing to clone without it */
    if (!('content' in document.createElement('template'))) return;

    for (var i = 0; i < mounts.length; i++) {
      var pn = Panel(mounts[i]);
      if (pn) panels.push(pn);
    }
    if (!panels.length) return;

    /* The overlay shells the mounts live inside. Their [hidden] flag is the
       only "am I on screen" signal upsell.js publishes, and watching the
       attribute keeps this island uncoupled from that one. No subtree, so the
       mounts this island hides and shows cannot re-trigger their own
       observer. */
    shells = all('[data-exon-popup], [data-exon-drawer]');
    /* keep in step with drops-anchor.js boot() (EXO-4824 D-1) */
    /* [data-no-offers] is written on the popup's MODAL, not on its shell
       (upsell.js), and it decides at runtime whether the popup row teases
       (leads to the cart) or opens: the modal is watched for it, so the row's
       go mark follows the mode rather than the next cart event. */
    var modals = all('[data-up-modal]');
    if (window.MutationObserver && (shells.length || modals.length)) {
      var mo = new MutationObserver(function () { repaint(); });
      for (var j = 0; j < shells.length; j++) {
        mo.observe(shells[j], { attributes: true, attributeFilter: ['hidden'] });
      }
      for (var k = 0; k < modals.length; k++) {
        mo.observe(modals[k], { attributes: true, attributeFilter: ['data-no-offers'] });
      }
    }

    document.addEventListener('exon:drops:state', repaint);
    document.addEventListener('exon:drops:choice-action', repaint);
    document.addEventListener('exon:cart', repaint);

    repaint();
    /* THE TICKET ASKS US, SO IT MUST BE ASKED AGAIN AFTER WE ANSWER. Document
       listeners fire in registration order and drops-ticket.js registered
       first, so it decided before this island had drawn anything. One
       follow-up repaint of the ticket, after ours, is what keeps "a choice
       drop replaces the ticket" true on the FIRST paint. */
    handOverToTicket();
  }

  function handOverToTicket() {
    var t = window.ExonDropTicket;
    if (t && typeof t.repaint === 'function') {
      try { t.repaint(); } catch (e) { /* the ticket's own fault channel has it */ }
    }
  }

  /* IS THIS SURFACE ACTUALLY DRAWING? drops-ticket.js asks before it stands
     down, and the honest answer is about MOUNTED, VISIBLE PANELS rather than
     about the mechanic string: a Mystery campaign whose island never mounted,
     whose writer is missing or whose hand is not renderable must leave the
     ticket in place, or an entitled member sees nothing at all. */
  function drawing() {
    for (var i = 0; i < panels.length; i++) {
      var el = panels[i].el;
      var m = el && el.parentNode;
      if (m && !m.hidden) return true;
    }
    return false;
  }

  try {
    boot();
  } catch (e) {
    fault('mystery_boot_threw', e);
  }

  /* exported for direct testing, the same way the bar and the ticket export
     their stages. `derive` is the pure one: (choice, nowMs, countdown, ctx)
     in, {view, reason} out, no DOM and no globals touched. */
  window.ExonDropMystery = { derive: derive, repaint: repaint, drawing: drawing };
})();
